import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import { useToast } from 'vue-toastification'
import { useAuthStore } from '@/stores/auth'
import { userHasAnyRole } from '@/composables/roles'
import { SUBSCRIPTION_PATH } from '@/lib/platformBilling'

/**
 * Central axios configuration and the session-expiry interceptor.
 *
 * Authentication is Sanctum's SPA mode: Laravel issues a session cookie and the
 * browser sends it. There is no token in JavaScript, so there is nothing here to
 * store, rotate or refresh — a request either carries a live session or it
 * doesn't, and the only recovery is signing in again.
 */

/**
 * Reads the API base URL from the environment, falling back to local dev.
 *
 * Note this already includes the `/api/v1` prefix. Anything building a URL by
 * hand — social sign-in navigates the browser rather than using axios — must
 * append to this, not re-add the prefix.
 */
export const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8085/api/v1'

const baseURL = apiBaseUrl

/**
 * `/sanctum/csrf-cookie` is served from the application root, *not* from under
 * the API prefix, so it needs its own base URL. Deriving the origin from the API
 * URL keeps that correct whether the SPA is same-origin with Laravel or sitting
 * on a sibling subdomain. A relative `baseURL` means same-origin, where the
 * default base already works.
 */
const appOrigin = (() => {
    try {
        return new URL(baseURL).origin
    } catch {
        return ''
    }
})()

/**
 * Endpoints where a 401 means "those credentials are wrong", not "your session
 * ended". Without this, a failed login would clear the store and redirect,
 * swallowing the error the login form is trying to display.
 */
const CREDENTIAL_ENDPOINTS = ['/login', '/register', '/forgotten-password', '/reset-password']

/** Marks a request already replayed once, so a stale CSRF token can't loop. */
type Replayable = InternalAxiosRequestConfig & { _csrfReplayed?: boolean }

/**
 * One in-flight CSRF request at a time.
 *
 * Several queries run in parallel on the dashboard, so a batch of them expiring
 * together would otherwise fire a `csrf-cookie` request each. Callers share the
 * pending promise instead.
 */
let csrfRequest: Promise<void> | null = null

/**
 * Primes the `XSRF-TOKEN` cookie. Must precede the first stateful write —
 * axios mirrors the cookie into the `X-XSRF-TOKEN` header from then on, which
 * is what `withXSRFToken` below turns on.
 */
export function primeCsrfCookie(): Promise<void> {
    if (!csrfRequest) {
        csrfRequest = axios
            .get('/sanctum/csrf-cookie', { baseURL: appOrigin })
            .then(() => undefined)
            .finally(() => {
                csrfRequest = null
            })
    }

    return csrfRequest
}

function isCredentialAttempt(url: string | undefined): boolean {
    if (!url) return false
    return CREDENTIAL_ENDPOINTS.some((endpoint) => url.endsWith(endpoint))
}

/** The toast id for a 402, so a repeat replaces the toast and does not stack. */
export const PLATFORM_LOCKED_TOAST_ID = 'platform-locked'

/** How long after one 402 toast to stay quiet. A page can send several writes at once. */
const PLATFORM_LOCKED_QUIET_MS = 10_000

let lastPlatformLockedToast = 0

/** Clears the quiet period. For tests only. */
export function resetPlatformLockedNotice() {
    lastPlatformLockedToast = 0
}

/**
 * Tells the user why a write was refused with 402.
 *
 * A 402 means the gym's Manajits subscription is not active and the API has
 * made the gym read-only. The session is fine, so this must never sign
 * anyone out. Only an admin or manager can pay, so only they get the route
 * to the subscription page. The router is imported on click, not at load:
 * the router imports the auth store, which imports this file.
 */
function notifyPlatformLocked() {
    const now = Date.now()
    if (now - lastPlatformLockedToast < PLATFORM_LOCKED_QUIET_MS) return
    lastPlatformLockedToast = now

    const canPay = userHasAnyRole(useAuthStore().user, ['admin', 'manager'])
    const message = 'Your Manajits subscription is not active. Manajits is read-only.'

    useToast().error(canPay ? `${message} Select this message to open Manajits plan.` : message, {
        id: PLATFORM_LOCKED_TOAST_ID,
        timeout: 8000,
        onClick: canPay
            ? () => {
                  import('@/router').then(({ default: router }) => router.push(SUBSCRIPTION_PATH))
              }
            : undefined
    })
}

/** Installs axios defaults and the shared response interceptor. */
export function configureHttp() {
    axios.defaults.baseURL = baseURL
    // Send the session cookie, and mirror the XSRF-TOKEN cookie into the
    // X-XSRF-TOKEN header. Sanctum's SPA mode needs both.
    axios.defaults.withCredentials = true
    axios.defaults.withXSRFToken = true
    axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'
    // Sanctum expects this to treat the request as coming from the SPA.
    axios.defaults.headers.common['Accept'] = 'application/json'

    axios.interceptors.response.use(
        (response) => response,
        async (error: AxiosError) => {
            // `error.response` is absent for network/timeout failures, so guard
            // it — reading through it unconditionally throws a TypeError here
            // and masks the original error.
            const status = error.response?.status
            const config = error.config as Replayable | undefined

            if (!status || !config || isCredentialAttempt(config.url)) {
                return Promise.reject(error)
            }

            // Payment required: the gym is read-only, not signed out. The
            // caller still gets the rejection, with the summary as `data`.
            if (status === 402) {
                notifyPlatformLocked()
                return Promise.reject(error)
            }

            /*
             * 419 is Laravel's CSRF/session-mismatch status, and it has two
             * causes: a stale token on a live session, or a session that has
             * gone. The first is recoverable without signing anyone out, so
             * prime a fresh cookie and replay once. If the session really is
             * gone the replay comes back 401 and falls through below.
             */
            if (status === 419 && !config._csrfReplayed) {
                config._csrfReplayed = true

                try {
                    await primeCsrfCookie()
                    return await axios(config)
                } catch (replayError) {
                    useAuthStore().forceLogout()
                    return Promise.reject(replayError)
                }
            }

            if (status === 401 || status === 419) {
                useAuthStore().forceLogout()
            }

            return Promise.reject(error)
        }
    )
}

/**
 * Confirms the session cookie on boot and loads the user behind it.
 *
 * The store persists the previous user, so the app renders optimistically while
 * this resolves; if the cookie is dead the interceptor sends the user to the
 * login screen. Callers don't need the result, only that it has been attempted.
 */
export async function restoreSession(): Promise<void> {
    try {
        await useAuthStore().profile()
    } catch {
        // A 401 or 419 has already been handled by the interceptor. Anything
        // else — offline, 5xx — leaves the app unauthenticated without
        // asserting a reason it can't know.
    }
}
